import type { ReactNode } from 'react';
import { cx } from './primitives/cx';
import { useShellTier } from '../../layouts/shellTier';

/**
 * P-076 (P2) · Una lista etiqueta/valor de verdad (`dl` con `dt` + `dd` por par). Normaliza los
 * `detail-list` de la piel anterior, que pintaban de 13 a 24 pares con el mismo peso: aquí los pares con
 * `emphasis` son los que mandan (resultado, importe) y los `muted` son metadata. En el teléfono la
 * etiqueta va a la izquierda y el valor a la derecha; `stack` pone la etiqueta encima (valores largos:
 * notas, motivos); `columns` reparte los pares en columnas desde 768 px.
 */
export interface MetaItem {
  label: ReactNode;
  value: ReactNode;
  emphasis?: boolean;
  muted?: boolean;
  key?: string;
}

export function MetaList({ items, columns = false, stack = false, className, 'aria-label': ariaLabel }: {
  items: MetaItem[];
  columns?: boolean;
  stack?: boolean;
  className?: string;
  'aria-label'?: string;
}) {
  return (
    <dl aria-label={ariaLabel} className={cx('n3-meta', columns && 'n3-meta--columns', stack && 'n3-meta--stack', className)}>
      {items.map((item, index) => (
        <div className={cx('n3-meta__item', item.emphasis && 'n3-meta__item--emphasis', item.muted && 'n3-meta__item--muted')} key={item.key ?? index}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Un título pequeño entre grupos de pares («Cifras del turno», «Quién y cuándo»). */
export function MetaHeading({ children, className }: { children: ReactNode; className?: string }) {
  return <h4 className={cx('n3-meta__heading', className)}>{children}</h4>;
}

/**
 * La metadata plegada: un `details` nativo (Chromium 83 lo entiende) con «Ver más datos». Lo que va aquí es
 * secundario por definición: nunca el resultado, el estado, un importe, una advertencia ni una acción.
 */
export function MetaDisclosure({ label = 'Ver más datos', items, columns = false, stack = false, defaultOpen = false, className }: {
  label?: string;
  items: MetaItem[];
  columns?: boolean;
  stack?: boolean;
  defaultOpen?: boolean;
  className?: string;
}) {
  // Codex R2 · H3: en tableta y escritorio no sobra espacio que ganar y nada debe quedar inicialmente oculto: abre ya.
  const tier = useShellTier();
  return (
    <details className={cx('n3-meta__more', className)} open={defaultOpen || tier !== 'compact' || undefined}>
      <summary>{label}</summary>
      <MetaList columns={columns} items={items} stack={stack} />
    </details>
  );
}
